/**
 * Die Routen der Seite „Deine Daten“ (Bahn A, server/daten-export-routen.ts). (17.10.2026)
 *
 * Alle Aufrufe mit der Sitzung (Cookie). Fehler werden zu festen deutschen Sätzen — nie ein Rohtext des Servers
 * auf dem Bildschirm. Die Seite bekommt diese Funktionen als Objekt (`DatenApi`), damit Tests Attrappen reichen.
 */
import {
  DATEN_KOPF,
  DATEN_KOPF_WERT,
  DATEN_PFAD_EXPORT,
  DATEN_PFAD_KONTO_LOESCHUNG,
  DATEN_PFAD_UEBERSICHT,
  type DatenUebersicht,
  type ExportStand,
  type ExportTeil,
} from "../../../shared/daten-export.js";

export interface DatenApi {
  uebersicht(): Promise<DatenUebersicht>;
  stand(): Promise<ExportStand>;
  starten(teile: ExportTeil[]): Promise<ExportStand>;
  loeschen(id: string): Promise<void>;
  kontoLoeschungAnfragen(): Promise<void>;
}

/** Ein Fehler mit einem Satz, der so auf dem Bildschirm stehen darf. */
export class DatenFehler extends Error {}

const SAETZE: Record<number, string> = {
  400: "Das war keine gültige Auswahl.",
  401: "Bitte neu anmelden.",
  403: "Dieses Konto darf das gerade nicht.",
  409: "Ein Export läuft schon.",
  410: "Der Export ist abgelaufen.",
  429: "Heute schon dreimal — morgen wieder.",
  507: "Auf dem Server ist gerade zu wenig Platz.",
};

async function ruf<T>(url: string, init?: RequestInit): Promise<T> {
  let r: Response;
  try {
    r = await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    throw new DatenFehler("Keine Verbindung zum Server.");
  }
  if (!r.ok) throw new DatenFehler(SAETZE[r.status] ?? "Das hat nicht geklappt. Bitte später noch einmal.");
  if (r.status === 204) return undefined as T;
  try {
    return (await r.json()) as T;
  } catch {
    return undefined as T;
  }
}

/** Schreibende Aufrufe brauchen den Schutzkopf und JSON — sonst antwortet der Server mit 403. */
const SCHREIB_KOPF = { "Content-Type": "application/json", [DATEN_KOPF]: DATEN_KOPF_WERT };
const schreibe = (method: "POST" | "DELETE", body: unknown): RequestInit => ({ method, headers: SCHREIB_KOPF, body: JSON.stringify(body) });

export const datenApi: DatenApi = {
  uebersicht: () => ruf<DatenUebersicht>(DATEN_PFAD_UEBERSICHT),
  stand: () => ruf<ExportStand>(DATEN_PFAD_EXPORT),
  starten: (teile) => ruf<ExportStand>(DATEN_PFAD_EXPORT, schreibe("POST", { teile })),
  loeschen: async (id) => {
    await ruf<void>(`${DATEN_PFAD_EXPORT}/${encodeURIComponent(id)}`, schreibe("DELETE", {}));
  },
  kontoLoeschungAnfragen: async () => {
    await ruf<unknown>(DATEN_PFAD_KONTO_LOESCHUNG, schreibe("POST", {}));
  },
};
